import { useRef, useState, useCallback, useEffect } from 'react';
import { prefersReducedMotion, getMotionSafeDuration } from '../../utils/motionUtils';

export interface CarouselState {
  canScrollLeft: boolean;
  canScrollRight: boolean;
  isScrolling: boolean;
  currentIndex: number;
}

export interface UseCarouselOptions {
  scrollAmount?: number;
  animationDuration?: number;
  autoUpdateInterval?: number;
}

export interface UseCarouselReturn {
  scrollContainerRef: React.RefObject<HTMLUListElement>;
  carouselState: CarouselState;
  scrollLeft: () => void;
  scrollRight: () => void;
  scrollToIndex: (index: number) => void;
  updateScrollState: () => void;
  handleScroll: () => void;
  handleKeyDown: (event: React.KeyboardEvent) => void;
  announceNavigation: (direction: 'left' | 'right' | 'home' | 'end') => void;
}

/**
 * useCarousel - Custom hook for managing carousel state and interactions
 * 
 * Features:
 * - Scroll state management (left/right availability)
 * - Smooth scrolling with configurable animation duration
 * - Keyboard navigation support
 * - Auto-update scroll state on container changes
 * - Index-based scrolling for precise positioning
 * - Performance optimized with useCallback
 * - Motion-safe animations that respect user preferences
 */
export const useCarousel = (options: UseCarouselOptions = {}): UseCarouselReturn => {
  const {
    scrollAmount = 1,
    animationDuration = 300,
    autoUpdateInterval = 100
  } = options;

  const reduceMotion = prefersReducedMotion();
  const baseScrollDuration = getMotionSafeDuration(animationDuration);
  const indexScrollDuration = getMotionSafeDuration(animationDuration + 200);
  const resolvedUpdateInterval = Math.max(autoUpdateInterval, 16);

  const scrollContainerRef = useRef<HTMLUListElement>(null);
  const liveAnnouncementRef = useRef<HTMLDivElement | null>(null);
  const announcementTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerMetricsRef = useRef<{ cardWidth: number; gap: number } | null>(null);
  const containerMetricsTargetRef = useRef<HTMLUListElement | null>(null);
  const containerMetricsDirtyRef = useRef(true);
  const [carouselState, setCarouselState] = useState<CarouselState>({
    canScrollLeft: false,
    canScrollRight: false,
    isScrolling: false,
    currentIndex: 0
  });

  const getContainerMetrics = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return null;
    }

    if (containerMetricsTargetRef.current !== container) {
      containerMetricsTargetRef.current = container;
      containerMetricsDirtyRef.current = true;
      containerMetricsRef.current = null;
    }

    if (!containerMetricsDirtyRef.current && containerMetricsRef.current) {
      return { container, ...containerMetricsRef.current };
    }

    const firstChild = container.children[0] as HTMLElement | undefined;
    const measuredWidth = firstChild?.getBoundingClientRect().width;
    const cardWidth = measuredWidth && measuredWidth > 0 ? measuredWidth : 200;

    let gap = 16;
    try {
      if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
        const style = window.getComputedStyle(container);
        const gapValue = style.columnGap || style.gap || '';
        const parsed = parseFloat(gapValue);
        if (Number.isFinite(parsed)) {
          gap = parsed;
        }
      }
    } catch {
      // ignore and fall back to default gap
    }

    containerMetricsRef.current = { cardWidth, gap };
    containerMetricsDirtyRef.current = false;
    return { container, cardWidth, gap };
  }, []);

  // Update scroll state based on container scroll position
  const updateScrollState = useCallback(() => {
    const metrics = getContainerMetrics();
    if (!metrics) return;
    const { container, cardWidth, gap } = metrics;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    
    // Add small tolerance for rounding errors and smooth scrolling
    const tolerance = 2;
    const canScrollLeft = scrollLeft > tolerance;
    const canScrollRight = scrollLeft < scrollWidth - clientWidth - tolerance;

    // Calculate current index based on scroll position
    const currentIndex = Math.round(scrollLeft / (cardWidth + gap));

    // Only update state if values have actually changed to prevent unnecessary re-renders
    setCarouselState(prev => {
      if (
        prev.canScrollLeft === canScrollLeft &&
        prev.canScrollRight === canScrollRight &&
        prev.currentIndex === currentIndex
      ) {
        return prev;
      }

      return {
        ...prev,
        canScrollLeft,
        canScrollRight,
        currentIndex
      };
    });
  }, [getContainerMetrics]);

  // Smooth scroll using CSS transforms instead of native scrolling
  const scrollLeft = useCallback(() => {
    const metrics = getContainerMetrics();
    if (!metrics || carouselState.isScrolling) return;
    const { container, cardWidth, gap } = metrics;

    setCarouselState(prev => ({ ...prev, isScrolling: true }));

    const scrollDistance = (cardWidth + gap) * scrollAmount;

    // Get current scroll position
    const currentScroll = container.scrollLeft;
    const targetScroll = Math.max(0, currentScroll - scrollDistance);

    // Use requestAnimationFrame for smooth animation
    const startTime = performance.now();
    const duration = baseScrollDuration;
    if (reduceMotion || duration <= 0) {
      container.scrollLeft = targetScroll;
      setCarouselState(prev => ({ ...prev, isScrolling: false }));
      updateScrollState();
      return;
    }
    const startScroll = currentScroll;
    const scrollDiff = targetScroll - startScroll;

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Use easeOutCubic for smooth deceleration
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const newScrollLeft = startScroll + (scrollDiff * easeOutCubic);
      
      container.scrollLeft = newScrollLeft;
      
      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      } else {
        // Animation complete
        setCarouselState(prev => ({ ...prev, isScrolling: false }));
        updateScrollState();
      }
    };

    requestAnimationFrame(animateScroll);
  }, [getContainerMetrics, scrollAmount, carouselState.isScrolling, updateScrollState, baseScrollDuration, reduceMotion]);

  // Smooth scroll right using CSS transforms
  const scrollRight = useCallback(() => {
    const metrics = getContainerMetrics();
    if (!metrics || carouselState.isScrolling) return;
    const { container, cardWidth, gap } = metrics;

    setCarouselState(prev => ({ ...prev, isScrolling: true }));

    const scrollDistance = (cardWidth + gap) * scrollAmount;

    // Get current scroll position
    const currentScroll = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const targetScroll = Math.min(maxScroll, currentScroll + scrollDistance);

    // Use requestAnimationFrame for smooth animation
    const startTime = performance.now();
    const duration = baseScrollDuration;
    if (reduceMotion || duration <= 0) {
      container.scrollLeft = targetScroll;
      setCarouselState(prev => ({ ...prev, isScrolling: false }));
      updateScrollState();
      return;
    }
    const startScroll = currentScroll;
    const scrollDiff = targetScroll - startScroll;

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Use easeOutCubic for smooth deceleration
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const newScrollLeft = startScroll + (scrollDiff * easeOutCubic);
      
      container.scrollLeft = newScrollLeft;
      
      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      } else {
        // Animation complete
        setCarouselState(prev => ({ ...prev, isScrolling: false }));
        updateScrollState();
      }
    };

    requestAnimationFrame(animateScroll);
  }, [getContainerMetrics, scrollAmount, carouselState.isScrolling, updateScrollState, baseScrollDuration, reduceMotion]);

  // Scroll to specific index using smooth custom animation
  const scrollToIndex = useCallback((index: number) => {
    const metrics = getContainerMetrics();
    if (!metrics || carouselState.isScrolling) return;
    const { container, cardWidth, gap } = metrics;

    setCarouselState(prev => ({ ...prev, isScrolling: true }));

    const targetScrollLeft = (cardWidth + gap) * index;

    // Get current scroll position
    const currentScroll = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const clampedTargetScroll = Math.max(0, Math.min(maxScroll, targetScrollLeft));

    // Use requestAnimationFrame for smooth animation
    const startTime = performance.now();
    const duration = indexScrollDuration;
    if (reduceMotion || duration <= 0) {
      container.scrollLeft = clampedTargetScroll;
      setCarouselState(prev => ({ ...prev, isScrolling: false }));
      updateScrollState();
      return;
    }
    const startScroll = currentScroll;
    const scrollDiff = clampedTargetScroll - startScroll;

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Use easeInOutCubic for smooth acceleration and deceleration
      const easeInOutCubic = progress < 0.5 
        ? 4 * progress * progress * progress 
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      
      const newScrollLeft = startScroll + (scrollDiff * easeInOutCubic);
      container.scrollLeft = newScrollLeft;
      
      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      } else {
        // Animation complete
        setCarouselState(prev => ({ ...prev, isScrolling: false }));
        updateScrollState();
      }
    };

    requestAnimationFrame(animateScroll);
  }, [getContainerMetrics, carouselState.isScrolling, updateScrollState, indexScrollDuration, reduceMotion]);

  // Handle keyboard navigation with focus management
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    // Only handle keyboard events if the carousel container is focused
    const target = event.target as HTMLElement;
    const currentTarget = event.currentTarget as HTMLElement;
    
    // If focus is on a show tile, let it handle its own keyboard events
    if (target !== currentTarget && target.getAttribute('role') === 'button') {
      return;
    }

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        scrollLeft();
        // Keep focus on the carousel container
        currentTarget.focus();
        break;
      case 'ArrowRight':
        event.preventDefault();
        scrollRight();
        // Keep focus on the carousel container
        currentTarget.focus();
        break;
      case 'Home':
        event.preventDefault();
        scrollToIndex(0);
        // Keep focus on the carousel container
        currentTarget.focus();
        break;
      case 'End':
        event.preventDefault();
        const container = scrollContainerRef.current;
        if (container) {
          const lastIndex = container.children.length - 1;
          scrollToIndex(lastIndex);
        }
        // Keep focus on the carousel container
        currentTarget.focus();
        break;
      case 'Tab':
        // Allow natural tab navigation to show tiles
        break;
    }
  }, [scrollLeft, scrollRight, scrollToIndex]);

  // Announce navigation actions for screen readers
  const announceNavigation = useCallback((direction: 'left' | 'right' | 'home' | 'end') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const totalItems = container.children.length;
    const currentIndex = carouselState.currentIndex;
    
    let message = '';
    switch (direction) {
      case 'left':
        message = `Scrolled left. Item ${Math.max(1, currentIndex)} of ${totalItems}`;
        break;
      case 'right':
        message = `Scrolled right. Item ${Math.min(totalItems, currentIndex + 2)} of ${totalItems}`;
        break;
      case 'home':
        message = `Jumped to beginning. Item 1 of ${totalItems}`;
        break;
      case 'end':
        message = `Jumped to end. Item ${totalItems} of ${totalItems}`;
        break;
    }

    // Clear any existing announcement before creating a new one
    if (announcementTimeoutRef.current) {
      clearTimeout(announcementTimeoutRef.current);
      announcementTimeoutRef.current = null;
    }
    if (liveAnnouncementRef.current) {
      document.body.removeChild(liveAnnouncementRef.current);
      liveAnnouncementRef.current = null;
    }

    // Create a temporary live region for announcements
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    liveAnnouncementRef.current = announcement;
    
    // Remove after announcement
    announcementTimeoutRef.current = setTimeout(() => {
      if (liveAnnouncementRef.current) {
        document.body.removeChild(liveAnnouncementRef.current);
        liveAnnouncementRef.current = null;
      }
      if (announcementTimeoutRef.current) {
        clearTimeout(announcementTimeoutRef.current);
        announcementTimeoutRef.current = null;
      }
    }, 1000);
  }, [carouselState.currentIndex]);

  // Handle scroll events to update arrow visibility
  const handleScroll = useCallback(() => {
    updateScrollState();
  }, [updateScrollState]);

  // Auto-update scroll state on mount and container changes
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Initial update
    containerMetricsDirtyRef.current = true;
    updateScrollState();

    // Set up ResizeObserver to handle container size changes
    const resizeObserver = new ResizeObserver(() => {
      containerMetricsDirtyRef.current = true;
      updateScrollState();
    });

    resizeObserver.observe(container);

    // Set up MutationObserver to handle content changes
    const mutationObserver = new MutationObserver(() => {
      containerMetricsDirtyRef.current = true;
      updateScrollState();
    });

    mutationObserver.observe(container, {
      childList: true,
      subtree: true
    });

    let intervalId: ReturnType<typeof setInterval> | null = null;
    if (resolvedUpdateInterval > 0) {
      intervalId = setInterval(() => {
        updateScrollState();
      }, resolvedUpdateInterval);
    }

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [updateScrollState, resolvedUpdateInterval]);

  // Cleanup any pending announcements on unmount
  useEffect(() => {
    return () => {
      if (announcementTimeoutRef.current) {
        clearTimeout(announcementTimeoutRef.current);
        announcementTimeoutRef.current = null;
      }
      if (liveAnnouncementRef.current) {
        document.body.removeChild(liveAnnouncementRef.current);
        liveAnnouncementRef.current = null;
      }
    };
  }, []);

  return {
    scrollContainerRef,
    carouselState,
    scrollLeft,
    scrollRight,
    scrollToIndex,
    updateScrollState,
    handleScroll,
    handleKeyDown,
    announceNavigation
  };
};
